import React from 'react';
import { Link } from 'react-router-dom';
import { Download, BookOpen, ArrowRight, Cpu, Boxes, Terminal } from 'lucide-react';
import { Button } from './ui/button';

const Hero = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-slate-950">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-40"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 pt-24 sm:pt-20">
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-10 items-center max-w-7xl mx-auto">
          {/* Columna texto */}
          <div className="xl:col-span-6 text-center xl:text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-medium mb-6">
              <Cpu className="w-3 h-3" />
              BLOX ESL · Embeddable Scripting Language
            </div>

            <h1 className="text-6xl sm:text-7xl xl:text-8xl font-bold text-white mb-4 leading-none tracking-tight">
              <span className="bg-gradient-to-r from-cyan-400 to-teal-400 text-transparent bg-clip-text">BLOX</span>
            </h1>

            <p className="text-2xl sm:text-3xl xl:text-4xl font-semibold text-white mb-6 leading-tight tracking-tight">
              Lenguaje de scripting <span className="text-cyan-400">embebible</span> con API C
            </p>

            <p className="text-base sm:text-lg text-slate-300 mb-4 leading-relaxed max-w-2xl mx-auto xl:mx-0">
              <span className="text-cyan-300 font-semibold">BLOX Core</span> es una VM/biblioteca embebible que integra scripts <code className="font-mono text-cyan-300">.blox</code> dentro de hosts nativos: aplicaciones C/C++, motores como Godot, servicios, simuladores industriales y firmware ESP32-S3.
            </p>

            <p className="text-slate-400 text-sm sm:text-base mb-8 max-w-2xl mx-auto xl:mx-0 leading-relaxed">
              El host conserva el control del proceso. BLOX aporta reglas, decisiones y comportamiento modificable sin recompilar.
            </p>

            {/* Frase guía */}
            <div className="hidden xl:block mb-8 pl-4 border-l-2 border-cyan-500/40">
              <p className="font-mono text-slate-400 text-sm">
                <span className="text-amber-400">C</span> controla el mundo.
                <br />
                <span className="text-cyan-400">BLOX</span> decide la lógica.
              </p>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center xl:justify-start gap-3">
              <Link to="/embedded-scripting-language">
                <Button size="lg" className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-slate-950 font-semibold text-base px-6 py-5 h-auto" data-testid="hero-cta-core">
                  <Boxes className="w-5 h-5 mr-2" />
                  Explorar BLOX Core
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/download">
                <Button size="lg" variant="outline" className="border-slate-700 text-white hover:bg-slate-800 text-base px-6 py-5 h-auto" data-testid="hero-cta-download">
                  <Download className="w-5 h-5 mr-2" />
                  Descargar
                </Button>
              </Link>
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-center xl:justify-start gap-x-6 gap-y-2 text-xs text-slate-500">
              <Link to="/docs" className="hover:text-cyan-400 transition-colors flex items-center gap-1">
                <BookOpen className="w-3 h-3" />
                Documentación
              </Link>
              <Link to="/cli" className="hover:text-cyan-400 transition-colors flex items-center gap-1" data-testid="hero-cli-link">
                <Terminal className="w-3 h-3" />
                BLOX CLI (herramienta derivada)
              </Link>
              <span className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                Build 2026-08-05 disponible
              </span>
            </div>
          </div>

          {/* Columna mockup embedding */}
          <div className="xl:col-span-6">
            <div className="relative max-w-xl mx-auto">
              {/* Host C */}
              <div className="rounded-xl bg-slate-900/90 backdrop-blur border border-amber-500/30 overflow-hidden shadow-2xl shadow-amber-500/5">
                <div className="flex items-center gap-2 px-4 py-2 bg-slate-950/80 border-b border-amber-900/40">
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
                  </div>
                  <span className="text-[10px] text-amber-400 ml-2 font-mono">host.c</span>
                  <span className="ml-auto text-[10px] text-slate-500 uppercase tracking-wider">Host nativo</span>
                </div>
                <pre className="p-4 overflow-x-auto text-slate-300 font-mono text-[13px] leading-relaxed">
<code><span className="text-slate-500">// El host crea la VM y llama BLOX</span>{"\n"}<span className="text-cyan-400">BloxVM</span> *vm = <span className="text-teal-400">blox_vm_create</span>();{"\n\n"}<span className="text-teal-400">blox_vm_register_native</span>(vm, <span className="text-amber-300">{'"HOST_LOG"'}</span>,{"\n"}    <span className="text-slate-500">1, ...,</span> host_log, <span className="text-slate-500">NULL</span>);{"\n\n"}<span className="text-teal-400">blox_vm_load_file</span>(vm, <span className="text-amber-300">{'"reglas.blox"'}</span>);{"\n"}<span className="text-teal-400">blox_vm_run_main</span>(vm);{"\n"}<span className="text-teal-400">blox_vm_call</span>(vm, <span className="text-amber-300">{'"evaluar_orden"'}</span>, ...);</code>
                </pre>
              </div>

              {/* Flecha bidireccional */}
              <div className="flex items-center justify-center py-2 gap-2 text-cyan-400" data-testid="hero-bidir-arrow">
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-cyan-500/40 to-cyan-500/60" />
                <div className="flex flex-col items-center text-[10px] font-mono px-2">
                  <span>↓ llama</span>
                  <span>↑ callback</span>
                </div>
                <div className="flex-1 h-px bg-gradient-to-l from-transparent via-cyan-500/40 to-cyan-500/60" />
              </div>

              {/* Script BLOX */}
              <div className="rounded-xl bg-slate-900/90 backdrop-blur border border-cyan-500/30 overflow-hidden shadow-2xl shadow-cyan-500/10">
                <div className="flex items-center gap-2 px-4 py-2 bg-slate-950/80 border-b border-cyan-900/40">
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
                  </div>
                  <span className="text-[10px] text-cyan-400 ml-2 font-mono">reglas.blox</span>
                  <span className="ml-auto text-[10px] text-slate-500 uppercase tracking-wider">Script BLOX</span>
                </div>
                <pre className="p-4 overflow-x-auto text-slate-300 font-mono text-[13px] leading-relaxed">
<code><span className="text-cyan-400">FUNCION</span> <span className="text-teal-400">evaluar_orden</span>(pedido){"\n"}<span className="text-cyan-400">INICIO</span>{"\n"}    <span className="text-cyan-400">SI</span> (pedido[<span className="text-amber-300">1</span>].subtotal {">"} <span className="text-amber-300">5000</span>) <span className="text-cyan-400">ENTONCES</span>{"\n"}        <span className="text-teal-400">HOST_LOG</span>(<span className="text-amber-300">{'"revision"'}</span>){"\n"}    <span className="text-cyan-400">FIN_SI</span>{"\n"}    <span className="text-cyan-400">RETORNAR</span> resultado{"\n"}<span className="text-cyan-400">FINAL_FUNCION</span></code>
                </pre>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
